import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import SummarySection from './SummarySection'
import { Link } from '@mui/material'
import ProfileSection, {
  PhrIntegrationProps,
  ShippingProps,
} from './ProfileSection'
import BillingSection from './BillingSection'
import { Billing, BankTransfer, ReservationDetailProps } from 'types/med'
import { DepartmentListItemProps } from 'types/common'
import Section from 'ui/layout/Section'
import { StackItem } from 'ui/layout/StackItem'
import PharmacyInfo from 'ui/partials/PharmacyInfo'
import { ReservationDateModalProps } from '../ReservationDateModal'
import TodokusuriText from 'ui/partials/TodokusuriText'
import { Text1 } from 'ui/shorthands/Texts'
import { todokusuriDocPath } from 'config/url'
import { howToReceiveMedicineDirectValue } from 'ui/partials/TodokusuriText'
import theme from 'ui/theme'
import { TayorisContent } from 'ui/pages/applicant/Reservation/Reservation'

const useStyles = makeStyles()(() => ({
  noSelection: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  todokusuriDescription: { paddingTop: theme.spacing(1) },
}))

export type RightPaneContentProps = {
  /** 予約情報 */
  reservation?: ReservationDetailProps | null

  /** 請求情報 */
  billings?: Billing[]

  /** 銀行決済情報 (reservation.settlement_method === 'bank_transfer'の場合に指定) */
  bankTransfer?: BankTransfer

  /** 編集可否 */
  editable?: boolean // TODO: 編集モードは将来的に実装

  shipping: ShippingProps

  phrIntegration: PhrIntegrationProps

  onChangeReservationDatetime: ReservationDateModalProps['onSubmit']

  departmentSetting?: DepartmentListItemProps
}

const ReservationDetail: React.FC<RightPaneContentProps> = ({
  reservation,
  billings,
  bankTransfer,
  shipping,
  phrIntegration,
  onChangeReservationDatetime,
  departmentSetting,
}: RightPaneContentProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()

  if (!reservation)
    return (
      <div className={classes.noSelection}>{t('予約が選択されていません')}</div>
    )

  const isTayoris =
    reservation?.pharmacy_service === 'tayoris_health_management_support'
  const isTodokusuri = reservation?.pharmacy_service === 'todokusuri'

  const tayoRisContent = (
    <Section>
      <StackItem label={t('薬局選択')} alignTop>
        <TayorisContent />
      </StackItem>
    </Section>
  )

  const pharmacyContent = (
    <>
      {!isTodokusuri && reservation.pharmacy && (
        <Section>
          <StackItem
            label={t('薬局選択')}
            alignTop={theme.spacing(2.5)}
            childNoPadding
          >
            <PharmacyInfo
              showDetail
              pharmacy={reservation.pharmacy}
              type={'med'}
            />
          </StackItem>
        </Section>
      )}
      {!isTodokusuri && reservation?.prescription_medicine === 'direct' && (
        <Section>
          <StackItem label={t('薬局選択')} alignTop>
            <Text1>{howToReceiveMedicineDirectValue}</Text1>
          </StackItem>
        </Section>
      )}
      {isTodokusuri && (
        <Section>
          <StackItem label={t('薬局選択')} alignTop>
            <Text1>
              <TodokusuriText isMedical />
            </Text1>
            <div className={classes.todokusuriDescription}>
              <div>
                <Trans
                  i18nKey={
                    'trans.患者よりとどくすりサービスの利用希望がある場合には...'
                  }
                  components={{
                    Link: (
                      <Link
                        href={todokusuriDocPath}
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    ),
                  }}
                />
              </div>
            </div>
          </StackItem>
        </Section>
      )}
    </>
  )

  return (
    <>
      <SummarySection
        unit={departmentSetting?.reservation_frame_period_minutes || 30}
        reservation={reservation}
        onChangeReservationDatetime={onChangeReservationDatetime}
      />
      <ProfileSection
        reservation={reservation}
        {...shipping}
        {...phrIntegration}
      />
      <BillingSection
        reservation={reservation}
        billings={billings}
        bankTransfer={bankTransfer}
        type={'medical'}
      />
      {isTayoris ? tayoRisContent : pharmacyContent}
    </>
  )
}

export default ReservationDetail
